<template>
  <div class="content">
    <v-toolbar flat>
      <v-app-bar-nav-icon><v-icon>mdi-account-group-outline</v-icon></v-app-bar-nav-icon>
      <v-toolbar-title>
        <span v-on="canViewParentFederation ? { click: redirectToParentFederation } : {}" v-bind:class="{ link: canViewParentFederation }">{{ federation.name }}</span>
        ->
        <span v-on="canViewParentDiscipline ? { click: redirectToParentDiscipline } : {}" v-bind:class="{ link: canViewParentDiscipline }">{{ discipline.name }}</span>
        ->
        <span v-on="canViewCurrentTeam ? { click: redirectToParentCoach } : {}" v-bind:class="{ link: canViewCurrentTeam }">{{ coachPublicName }}</span>
        ->
        <span>{{ team.name }}</span>
      </v-toolbar-title>
    </v-toolbar>

    <v-tabs v-model="tab">
      <v-tab to="athletes"><v-icon class="tab-icon">mdi-run</v-icon> Athlètes</v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab" style="overflow: visible; display: block">
      <v-tab-item id="athletes">
        <athletes :teamId_prop="teamId" />
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
import { VTabs } from "vuetify/lib";
import { getDisciplineAsync, getIsManagerPartOfDisciplineAsync, getIsCoachPartOfDisciplineAsync } from "@/api/disciplinesApi";
import { getTeamAsync, getIsUserPartOfTeamAsync, getCoachPublicNameAsync } from "@/api/teamApi";
import { getFederationByIdAsync, getIsUserPartOfFederationAsync } from "@/api/federationsApi";
import PermissionsService from "@/services/PermissionsService";
import _ from "lodash";
import store from "@/store";
import AuthService from "@/services/AuthService";
import athletes from "@/containers/TeamPanel/athletes";

export default {
  components: {
    VTabs,
    athletes,
  },

  props: {
    disciplineId_prop: [Number, String],
    federationId_prop: [Number, String],
    coachId_prop: [Number, String],
    teamId_prop: [Number, String],
  },

  watch: {
    $route() {
      this.initPage();
    },
  },

  data() {
    return {
      federationId: null,
      disciplineId: null,
      coachId: null,
      teamId: null,
      isCurrentUserPartOfFederation: false,
      isCurrentUserManagerOfDiscipline: false,
      isCurrentUserCoachOfDiscipline: false,
      isCurrentUserCoachOfTeam: false,
      isCurrentUserPartOfTeam: false,
      authService: AuthService,
      federation: {},
      discipline: {},
      coachPublicName: null,
      team: {},
      tab: null,
    };
  },

  computed: {
    canViewCurrentTeam() {
      // Can view own Team and is part of current Team
      let canViewOwnTeamAndIsPartOfCurrentTeam = PermissionsService.TeamsPermissionsService.canViewOwnTeam && this.isCurrentUserPartOfTeam;
      // Can view all Teams and is part of discipline (coach or manager) or part of federation
      let canViewAllTeams =
        PermissionsService.TeamsPermissionsService.canViewAllTeamsOwnDiscipline &&
        (this.isCurrentUserManagerOfDiscipline || this.isCurrentUserCoachOfDiscipline || this.isCurrentUserPartOfFederation);
      return canViewOwnTeamAndIsPartOfCurrentTeam || canViewAllTeams;
    },
    canManageCurrentTeam() {
      // Can manage own Team and is coach of current Team
      let canManageCurrentTeamAndIsPartOfCurrentTeam = PermissionsService.TeamsPermissionsService.canManageOwnTeam && this.isCurrentUserCoachOfDiscipline;
      // Can manage all Teams and is part of discipline (coach or manager) or part of federation
      let canManageAllTeams =
        PermissionsService.TeamsPermissionsService.canManageAllTeamsOwnDiscipline &&
        (this.isCurrentUserManagerOfDiscipline || this.isCurrentUserCoachOfDiscipline || this.isCurrentUserPartOfFederation);
      return canManageCurrentTeamAndIsPartOfCurrentTeam || canManageAllTeams;
    },
    canViewParentDiscipline() {
      // Can view own discipline and is part of current discipline
      let canViewOwnDisciplineAndIsPartOfCurrentDiscipline =
        PermissionsService.DisciplinesPermissionsService.canViewOwnDiscipline && this.isCurrentUserManagerOfDiscipline;
      // Can view all Disciplines
      let canViewAllDisciplines = PermissionsService.DisciplinesPermissionsService.canViewAllDisciplinesOwnFederation;
      return canViewOwnDisciplineAndIsPartOfCurrentDiscipline || canViewAllDisciplines;
    },
    canViewParentFederation() {
      // Can view own federation and is part of current federation
      let canViewOwnFederationAndIsPartOfCurrentFederation = PermissionsService.FederationsPermissionsService.canViewOwnFederation && this.isCurrentUserPartOfFederation;
      // Can view all federations
      let canViewAllFederations = PermissionsService.FederationsPermissionsService.canViewAllFederations;
      return canViewOwnFederationAndIsPartOfCurrentFederation || canViewAllFederations;
    },
  },

  async mounted() {
    await this.initPage();
  },

  methods: {
    async initPage() {
      // Override the props values from the url if provided
      this.federationId = this.$route.params.federationId ? parseInt(this.$route.params.federationId) : this.federationId_prop;
      this.disciplineId = this.$route.params.disciplineId ? parseInt(this.$route.params.disciplineId) : this.disciplineId_prop;
      this.coachId = this.$route.params.coachId ? this.$route.params.coachId : this.coachId_prop;
      this.teamId = this.$route.params.teamId ? parseInt(this.$route.params.teamId) : this.teamId_prop;

      // Check if the current user is part of current federation
      this.isCurrentUserPartOfFederation = await getIsUserPartOfFederationAsync(this.authService.userId, this.federationId);
      // Check if the current user is part of current discipline
      this.isCurrentUserCoachOfDiscipline = await getIsCoachPartOfDisciplineAsync(this.authService.userId, this.disciplineId);
      // Check if the current user is part of current discipline
      this.isCurrentUserManagerOfDiscipline = await getIsManagerPartOfDisciplineAsync(this.authService.userId, this.disciplineId);
      // Check if the current user is part of current team
      this.isCurrentUserPartOfTeam = await getIsUserPartOfTeamAsync(this.authService.userId, this.teamId);

      // Check if the user can see this page
      if (!this.canViewCurrentTeam) {
        store.dispatch("setSnackbar", {
          title: `Non Autorisé`,
          text: `Vous n'êtes pas autorisé à consulter cette ressource.`,
          status: "warning",
          timeout: 2000,
        });

        this.$router.replace("/");
      }

      this.team = await getTeamAsync(this.teamId);
      let coachPublicNameResult = await getCoachPublicNameAsync(this.coachId);
      this.coachPublicName = coachPublicNameResult.value;
      this.discipline = await getDisciplineAsync(this.disciplineId);
      this.federation = await getFederationByIdAsync(this.federationId);
    },

    // Redirect to parent federation
    redirectToParentFederation() {
      this.$router.push({
        name: "FederationPanel",
        params: {
          id: this.federation.id,
          tab: "users",
        },
      });
    },

    // Redirect to parent discipline
    redirectToParentDiscipline() {
      this.$router.push({
        name: "DisciplinePanel",
        params: {
          federationId: this.federationId,
          disciplineId: this.discipline.id,
          tab: "disciplineManagers",
        },
      });
    },

    // Redirect to parent coach
    redirectToParentCoach() {
      this.$router.push({
        name: "CoachPanel",
        params: {
          federationId: this.federationId,
          disciplineId: this.disciplineId,
          coachId: this.coachId,
          tab: "teams",
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.blue-ENE {
  color: rgb(15, 21, 71) !important;
}
.green-GCE {
  color: rgb(27, 138, 98) !important;
}
.tab-icon {
  padding-right: 5px;
}
.link {
  color: rgb(15, 21, 71);
  text-decoration: underline;
  text-underline-offset: 5px;
  text-decoration-color: rgb(15, 21, 71);
  cursor: pointer;

  &:hover {
    color: rgba(15, 21, 71, 0.786);
    text-decoration-color: rgba(15, 21, 71, 0.5);
  }
  &:focus {
    color: rgba(15, 21, 71, 0.786);
    text-decoration-color: rgb(15, 21, 71, 0.5);
  }
  &:active {
    color: rgba(15, 21, 71, 0.786);
    text-decoration-color: rgb(15, 21, 71, 0.5);
  }
}
</style>
